import { forwardRef, type HTMLAttributes } from 'react';
import cn from 'classnames';

import { StringXSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import { Tile } from '../Tile/Tile';
import { TilesBg } from '../TilesBg/TilesBg';

import css from './styles.module.scss';

import tgemPng from '/img/tgem.png';
import tgemWebp from '/img/tgem.webp';

interface CoinTileProps extends HTMLAttributes<HTMLDivElement> {
  isSuperGame?: boolean;
  isActive?: boolean;
  variant?: 'mini-game' | 'start' | 'default';
  value?: number;
}

const CoinTile = forwardRef<HTMLDivElement, CoinTileProps>(
  ({ variant = 'default', value, isSuperGame, ...props }, ref) => {
    const bgVariants = {
      'mini-game': 'purple-fill',
      'start': 'start',
      'default': 'purple',
    } as const;

    const activeBgVariants = {
      'mini-game': 'purple-active',
      'start': 'start',
      'default': 'purple-active',
    } as const;

    return (
      <Tile
        ref={ref}
        {...props}
        variant={props.isActive ? 'dark' : 'grey'}
        className={cn(props.className, 'flex items-center justify-center')}
      >
        {variant === 'start' && (
          <StringXSmall
            weight="bold"
            className="absolute z-[3] left-1/2 -translate-x-1/2 top-[7.14%]"
          >
            Start
          </StringXSmall>
        )}
        {value === 0 && variant === 'mini-game' ? (
          <span
            className={cn(
              'font-black text-[2rem] text-[#E8DBFB] leading-none relative z-[2]',
              css.zeroShadow,
            )}
          >
            0
          </span>
        ) : (
          <div className="relative z-[2] w-[80%] h-[80%] flex">
            <Picture
              className="w-full h-full"
              img={tgemPng}
              webp={tgemWebp}
              alt="tgem"
            />
          </div>
        )}
        {!!value && (
          <div className={css.tileValue}>
            <StringXSmall weight="bold">{value}</StringXSmall>
          </div>
        )}
        <TilesBg
          className={cn(!props.isActive && '!top-0 !left-0 !w-full !h-full')}
          variant={
            props.isActive
              ? activeBgVariants[variant]
              : value === 0 && variant === 'mini-game'
                ? bgVariants.default
                : bgVariants[variant]
          }
          withStarsBg={
            (variant === 'mini-game' && value !== 0) || variant === 'start'
          }
        />
      </Tile>
    );
  },
);

export { CoinTile };
